import { yupResolver } from '@hookform/resolvers/yup'
import { LineInfoCircle } from '@qr-front/icons/line-info-circle'
import { LinePlus } from '@qr-front/icons/line-plus'
import { Button } from '@qr-front/ui/components/Button'
import { URL_MAX_LENGTH } from 'constants/validationConstants'
import { useSetAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { transformBackendResponseKey } from '@/config'
import { useSyncFieldWithAtom } from '@/hooks'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { maxLength, validateContentForm } from '@/utils'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../../components/GeneratorFormSection'
import { RhcField } from '../../components/RhcField'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import { ButtonsSection, imagesInfoButtonListAtom } from './ButtonsSection'
import {
	getImagesInfoFormSchema,
	TImagesInfoFields,
} from './imagesInfoFormModel'

export const IMAGES_INFO_FORM_ID = 'images-info'

export const imagesInfoHeadlineAtom = atomWithReset('')
export const imagesInfoWebsiteAtom = atomWithReset<string | undefined>('')
export const imagesInfoDescriptionAtom = atomWithReset<string | undefined>('')

export const resetImagesInfoForm = () => {
	getStore().set(imagesInfoHeadlineAtom, RESET)
	getStore().set(imagesInfoWebsiteAtom, RESET)
	getStore().set(imagesInfoDescriptionAtom, RESET)
	getStore().set(imagesInfoButtonListAtom, RESET)
}

export const ImagesInfoForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	const form = useForm<TImagesInfoFields>({
		defaultValues: {
			headline: getStore().get(imagesInfoHeadlineAtom),
			info: {
				website: getStore().get(imagesInfoWebsiteAtom),
			},
			description: getStore().get(imagesInfoDescriptionAtom),
			buttons: getStore().get(imagesInfoButtonListAtom),
		},
		mode: 'all',
		resolver: yupResolver(getImagesInfoFormSchema(t)),
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	const {
		formState: { isValid },
		handleSubmit,
	} = form

	useSyncFormSectionValidity({ formId: IMAGES_INFO_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				const translatedMessage = t(
					transformBackendResponseKey(message),
				)
				form.setError(property as Parameters<typeof form.setError>[0], {
					message: translatedMessage,
				})
			},
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) =>
						handleSubmit(onValid, async (errors) => {
							onInvalid(errors)
						}),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit],
	)

	const { getValues, setFocus, setValue } = form

	useSyncFieldWithAtom({
		atom: imagesInfoButtonListAtom,
		setFieldValue: (value) => setValue('buttons', value),
	})

	const headlineField = (
		<RhcField
			type="text"
			required
			form={form}
			path="headline"
			fieldName="headline"
			atom={imagesInfoHeadlineAtom}
			label={t(
				'generator__content_form_section__images_information__headline__label',
			)}
			placeholder={t(
				'generator__content_form_section__images_information__headline__placeholder',
			)}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const websiteField = (
		<RhcField
			type="url"
			form={form}
			path="info.website"
			fieldName="website"
			atom={imagesInfoWebsiteAtom}
			label={t(
				'generator__content_form_section__images_information__website__label',
			)}
			placeholder={t('ui__input_placeholders__url')}
			className="col-span-2 tablet:col-span-1"
			onChangeTransform={(event) => maxLength(URL_MAX_LENGTH + 1)(event)}
		/>
	)

	const descriptionField = (
		<RhcField
			type="text"
			form={form}
			path="description"
			fieldName="description"
			atom={imagesInfoDescriptionAtom}
			label={t(
				'generator__content_form_section__images_information__description__label',
			)}
			placeholder={t(
				'generator__content_form_section__images_information__description__placeholder',
			)}
			className="col-span-2"
		/>
	)

	const setButtonList = useSetAtom(imagesInfoButtonListAtom)

	const handleClickAddButton = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}
		setButtonList((prev) => [...prev, { title: '', url: '' }])
		setTimeout(
			() => setFocus(`buttons.${getValues('buttons')!.length - 1}.title`),
			0,
		)
	}

	return (
		<fieldset>
			<div className="grid grid-cols-2 gap-x-8 gap-y-4">
				{headlineField}
				{websiteField}
				{descriptionField}
			</div>

			<FormProvider {...form}>
				<ButtonsSection />
			</FormProvider>

			<Button
				visual="soft"
				// Using pointer-down instead of click to avoid button shifting when active input loses focus and validates
				onPointerDown={handleClickAddButton}
				className="mt-6 tablet:mt-8 [&>svg]:size-4"
			>
				<LinePlus />
				{t(
					'generator__content_form_section__images_information__add_button',
				)}
			</Button>
		</fieldset>
	)
})

ImagesInfoForm.displayName = 'ImagesInfoForm'

export const imagesInfoFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: IMAGES_INFO_FORM_ID,
	title: t('generator__content_form_section__images_information__title'),
	description: t(
		'generator__content_form_section__images_information__description',
	),
	icon: <LineInfoCircle />,
	Content: ImagesInfoForm,
})
